import React, { useRef, useState, useCallback, useEffect } from 'react'

// SVG center in SVG coordinate space (the <g> is translated to 350,350)
const SVG_CENTER = 350
const MIN_DRAG_PX = 5

// Convert client (screen) coordinates to SVG coordinate space.
// Uses getScreenCTM which correctly handles CSS transforms, scaling, and iframes.
function clientToSVG(
  clientX: number,
  clientY: number,
  svg: SVGSVGElement,
): { x: number; y: number } | null {
  const ctm = svg.getScreenCTM()
  if (!ctm) return null
  const point = new DOMPoint(clientX, clientY)
  const svgPoint = point.matrixTransform(ctm.inverse())
  return { x: svgPoint.x, y: svgPoint.y }
}

function getAngleFromSVGPoint(svgX: number, svgY: number): number {
  return Math.atan2(svgY - SVG_CENTER, svgX - SVG_CENTER) * (180 / Math.PI)
}

function getClientPos(e: MouseEvent | TouchEvent): { clientX: number; clientY: number } | null {
  if ('touches' in e) {
    const touch = e.touches[0] ?? e.changedTouches[0]
    if (!touch) return null
    return { clientX: touch.clientX, clientY: touch.clientY }
  }
  return { clientX: e.clientX, clientY: e.clientY }
}

function isInsideWheel(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return target.closest('path') !== null
}

export default function useRotate() {
  const [rotation, setRotation] = useState(0)
  const dragging = useRef(false)
  const didDrag = useRef(false)
  const startPos = useRef<{ x: number; y: number } | null>(null)
  const touchTarget = useRef<EventTarget | null>(null)
  const lastAngle = useRef<number | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const groupRef = useRef<SVGGElement | null>(null)

  const onPointerMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!dragging.current || lastAngle.current === null || !svgRef.current || !startPos.current)
      return
    if ('touches' in e) e.preventDefault()
    const pos = getClientPos(e)
    if (!pos) return

    // Don't start rotating until pointer has moved enough pixels
    const dx = pos.clientX - startPos.current.x
    const dy = pos.clientY - startPos.current.y
    if (!didDrag.current && dx * dx + dy * dy < MIN_DRAG_PX * MIN_DRAG_PX) return
    didDrag.current = true

    const svgPt = clientToSVG(pos.clientX, pos.clientY, svgRef.current)
    if (!svgPt) return
    const angle = getAngleFromSVGPoint(svgPt.x, svgPt.y)
    const delta = angle - lastAngle.current
    setRotation((r) => r + delta)
    lastAngle.current = angle
  }, [])

  const onPointerEnd = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const wasDrag = didDrag.current
      dragging.current = false
      lastAngle.current = null
      startPos.current = null
      document.removeEventListener('mousemove', onPointerMove)
      document.removeEventListener('touchmove', onPointerMove)
      // eslint-disable-next-line react-hooks/immutability
      document.removeEventListener('mouseup', onPointerEnd)
      document.removeEventListener('touchend', onPointerEnd)
      document.removeEventListener('touchcancel', onPointerEnd)

      // Synthesize click for taps since preventDefault on touchstart suppresses it
      if (!wasDrag && 'changedTouches' in e && touchTarget.current) {
        const target = touchTarget.current as Element
        const touch = e.changedTouches[0]
        if (touch && target.dispatchEvent) {
          target.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              clientX: touch.clientX,
              clientY: touch.clientY,
            }),
          )
        }
      }
      touchTarget.current = null
    },
    [onPointerMove],
  )

  const startDrag = useCallback(
    (clientX: number, clientY: number, target: EventTarget | null) => {
      const svg = groupRef.current?.closest('svg')
      if (!svg) return
      if (!isInsideWheel(target)) return
      svgRef.current = svg
      dragging.current = true
      didDrag.current = false
      startPos.current = { x: clientX, y: clientY }
      const svgPt = clientToSVG(clientX, clientY, svg)
      lastAngle.current = svgPt
        ? getAngleFromSVGPoint(svgPt.x, svgPt.y)
        : Math.atan2(clientY, clientX) * (180 / Math.PI)
      document.addEventListener('mousemove', onPointerMove)
      document.addEventListener('mouseup', onPointerEnd)
      document.addEventListener('touchmove', onPointerMove, { passive: false })
      document.addEventListener('touchend', onPointerEnd)
      document.addEventListener('touchcancel', onPointerEnd)
    },
    [onPointerMove, onPointerEnd],
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      startDrag(e.clientX, e.clientY, e.target)
    },
    [startDrag],
  )

  // Suppress click events that follow a drag rotation
  useEffect(() => {
    const el = groupRef.current
    if (!el) return

    const handleClick = (e: Event) => {
      if (didDrag.current) {
        e.stopPropagation()
        didDrag.current = false
      }
    }

    el.addEventListener('click', handleClick, { capture: true })
    return () => {
      el.removeEventListener('click', handleClick, { capture: true })
    }
  }, [])

  // Attach touchstart directly to the DOM element with { passive: false }
  // so preventDefault() actually prevents scrolling on mobile.
  useEffect(() => {
    const el = groupRef.current
    if (!el) return

    const handleTouchStart = (e: TouchEvent) => {
      const pos = getClientPos(e)
      if (!pos) return
      if (!isInsideWheel(e.target)) return
      e.preventDefault()
      touchTarget.current = e.target
      startDrag(pos.clientX, pos.clientY, e.target)
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: false })
    return () => {
      el.removeEventListener('touchstart', handleTouchStart)
    }
  }, [startDrag])

  useEffect(() => {
    return () => {
      if (dragging.current) {
        document.removeEventListener('mousemove', onPointerMove)
        document.removeEventListener('mouseup', onPointerEnd)
        document.removeEventListener('touchmove', onPointerMove)
        document.removeEventListener('touchend', onPointerEnd)
        document.removeEventListener('touchcancel', onPointerEnd)
      }
    }
  }, [onPointerMove, onPointerEnd])

  return { rotation, onMouseDown, groupRef } as const
}
